'use client';
/**
 * Hook that provides client-side Turnkey signing functions for embedded/imported wallets.
 *
 * Relocated from frontend/src/hooks/auth/use-turnkey-signing.ts as part of the
 * auth boundary migration (spec 009 FILE 14 Group E).
 *
 * Lives in src/platform/web/auth/ because it imports @turnkey/react-wallet-kit
 * (React DOM, iframe) — a web-only vendor SDK. The raw Turnkey key/sign primitive
 * remains external (allowlist per Group E rule); this hook wraps it into the
 * SDK-owned SigningDeps interface.
 *
 * Uses the Turnkey SDK client from useTurnkey() to sign transactions and messages
 * directly in the browser. This replaces the backend server-side Turnkey signing
 * path, avoiding the "organization mismatch" error when the backend parent API key
 * tries to sign for SDK-created sub-org wallets.
 */
import { useTurnkey } from '@turnkey/react-wallet-kit';
import { fromHex, toHex } from '../../../../tx-engine/bytes.js';
/** Default no-op provider — callers that wire external wallets should pass their own. */
const NULL_PROVIDER = () => null;
/**
 * Creates SigningDeps that use client-side Turnkey signing for embedded/imported wallets.
 *
 * When the Turnkey httpClient is available (user is authenticated), signing goes
 * through the Turnkey SDK's signTransaction / signRawPayload APIs directly.
 * When not available (e.g. before login), the Turnkey signing functions are omitted
 * and the signing layer throws a clear error.
 *
 * `getExternalProvider` is injected as a prop so the hook remains platform-neutral
 * in its core logic — the caller (app code) passes the browser wallet detection
 * function from their platform context (e.g. `getExternalWalletProvider` from
 * `frontend/src/lib/wallet/external-wallet-provider.ts`).
 *
 * External-wallet callers MUST pass `getExternalProvider` — omitting it means
 * external wallet transactions will throw "External wallet provider not available".
 */
export function useTurnkeySigningDeps(getExternalProvider = NULL_PROVIDER) {
    const { httpClient } = useTurnkey();
    const turnkeySignTransaction = httpClient
        ? async (transaction, walletAddress) => {
            const unsignedTransaction = toHex(transaction);
            const result = await httpClient.signTransaction({
                signWith: walletAddress,
                unsignedTransaction,
                type: 'TRANSACTION_TYPE_SOLANA',
            });
            const signedTxHex = result.signedTransaction;
            if (!signedTxHex) {
                throw new Error('Turnkey signTransaction did not return a signed transaction');
            }
            return fromHex(signedTxHex);
        }
        : undefined;
    const turnkeySignMessage = httpClient
        ? async (message, walletAddress) => {
            // Turnkey's signRawPayload signs arbitrary bytes with the wallet's key.
            // For Ed25519 (Solana), hashFunction must be HASH_FUNCTION_NOT_APPLICABLE
            // per RFC 8032 — EdDSA does not support signing raw digests.
            const payload = toHex(message);
            const result = await httpClient.signRawPayload({
                signWith: walletAddress,
                payload,
                encoding: 'PAYLOAD_ENCODING_HEXADECIMAL',
                hashFunction: 'HASH_FUNCTION_NOT_APPLICABLE',
            });
            const r = result.r;
            const s = result.s;
            if (!r || !s) {
                throw new Error('Turnkey signRawPayload did not return r/s components');
            }
            // Concatenate r+s to form the Ed25519 signature (64 bytes)
            return fromHex(`${r}${s}`);
        }
        : undefined;
    return {
        getExternalProvider,
        turnkeySignTransaction,
        turnkeySignMessage,
    };
}
//# sourceMappingURL=use-turnkey-signing.js.map